import React from 'react';
import { Typography, Tooltip, SxProps, Theme } from '@mui/material';
import { useSnackbar } from 'notistack';

/**
 * CopyableIdCell — the canonical way to render any opaque identifier
 * (challan number, batch ID, invoice number, external reference…) in a table
 * row or detail panel. Wraps the value in a monospace Typography, adds a
 * copy-on-click behaviour with a tooltip prompt and a snackbar confirmation,
 * and stops the click from propagating to parent row handlers (so clicking
 * the number in a table row copies the number instead of opening the row's
 * detail dialog).
 */

interface Props {
  value: string | null | undefined;
  variant?: React.ComponentProps<typeof Typography>['variant'];
  fontWeight?: number;
  /** When true, render a shortened display (e.g. `9af86139…027eb` for a
   *  UUID) while keeping the full value as the copy payload. The tooltip
   *  surfaces the full value on hover. Defaults to false — opaque short
   *  numbers like challan numbers should show in full. */
  truncate?: boolean;
  /** How many characters to show at each end when truncating. The ellipsis
   *  sits between them. Only applied if `truncate` is true and the string
   *  is actually longer than head + tail + 1. */
  truncateHead?: number;
  truncateTail?: number;
  sx?: SxProps<Theme>;
}

const CopyableIdCell: React.FC<Props> = ({
  value,
  variant = 'body2',
  fontWeight = 600,
  truncate = false,
  truncateHead = 8,
  truncateTail = 4,
  sx,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const text = value ? String(value) : '';

  const shouldShorten = truncate && text.length > truncateHead + truncateTail + 1;
  // Display is purely visual — the copy payload is always `text` (full).
  const display = shouldShorten
    ? `${text.slice(0, truncateHead)}…${text.slice(-truncateTail)}`
    : text;

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Legacy fallback for older browsers / insecure contexts (dev over HTTP)
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      enqueueSnackbar(`Copied ${text}`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy — copy the value manually.', { variant: 'error' });
    }
  };

  if (!text) {
    return (
      <Typography component="span" variant={variant} sx={{ color: 'text.disabled' }}>
        —
      </Typography>
    );
  }

  // Tooltip reveals the full value when truncated so the user can read
  // the complete ID without clicking. Non-truncated case just prompts
  // the copy affordance.
  const tooltipTitle = shouldShorten ? text : 'Click to copy';

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <Typography
        component="span"
        variant={variant}
        onClick={copy}
        sx={{
          fontFamily: 'monospace',
          fontWeight,
          cursor: 'pointer',
          // When truncated, text-select would only grab the shortened display
          // (not the real value), which would be a quiet correctness bug.
          // Disable it so copy is only possible via click → full value.
          userSelect: shouldShorten ? 'none' : 'all',
          transition: 'color 120ms',
          '&:hover': {
            color: (theme) => theme.palette.primary.main,
          },
          ...(sx as any),
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  );
};

export default CopyableIdCell;
